// @ts-strict-ignore
import { holdsActiveCustomModel } from '../../../../../functions/client-model/client-node/holds-active-custom-model';
import { holdsCustomModel } from '../../../../../functions/client-model/client-node/holds-custom-model';
import { holdsInitialisedUtilisationTE } from '../../../../../functions/client-model/client-node/holds-initialised-utilisation-te';
import { isClientNode } from '../../../../../functions/client-model/client-node/is-client-node';
import { isLeafNode } from '../../../../../functions/client-model/client-node/is-leaf-node';
import { unclampedLerp } from '../../../../../functions/utilities/unclamped-lerp';
import { ClientNode } from '../../../../../model/client-model/client-node';
import { Dict } from '../../../../../model/dict';
import { MeasurementSystem } from '../../../../../model/enums/measurement-system';
import { PersistedTransitionProperties } from '../../../../../model/transition-targets/persisted/persisted-transition-properties';
import { Series } from '../../../../../modules/diagram/compound-renderers/chart-renderer/chart/series/series';
import { getChildren } from '../../../../../public/dashboard/shared/functions/get-children';
import { isClientBoardContainer } from '../../../../../public/dashboard/shared/functions/is-client-board-container';
import { ClientBoardContainer } from '../../../../../public/dashboard/shared/model/client-board-container';
import { DashboardTransitionData } from '../../../../../public/dashboard/shared/model/dashboard-transition-data';
import { CCCModelUtility } from '../../../../../services/climate-engine/ccc-model-utility/ccc-model-utility';
import { UnitConversionService } from '../../../../../services/unit-conversion/unit-conversion.service';
import { getLeafNodes } from '../../../functions/get-leaf-nodes';
import { YearByYearNodeData } from '../year-by-year/year-by-year.node-data';
import { YearByYearDataSeries } from './year-by-year-data-series';

import { getBoardYearsFromTimeline } from '../../../../../functions/timeline/get-board-years-from-timeline';
import { BoardTimeline } from '../../../../../model/board-timeline';
import { TransitionElement } from '../../../../../model/enums/transition-element';
import { ABATEMENT_SERIES_ID } from '../../../../../public/dashboard/shared/dashboard.constants';

export class YearByYearDataSeriesBuilder {
  private _boardYears: number[];
  private _calculatedSeries: Dict<YearByYearDataSeries> = {};
  //This will get cleaned up in a future task
  constructor(
    private _board: ClientBoardContainer,
    private _yearByYear: Dict<YearByYearNodeData>,
    private _historicalEmissions: Dict<number[]>,
    private _parentIds: Dict<string>,
    private _getTTUnit: (tt: TransitionElement, properties: PersistedTransitionProperties) => string,
    private _getTTKind: (tt: TransitionElement, properties: PersistedTransitionProperties) => string,
    private _timeline: BoardTimeline,
    private _outcomeLabel: string,
    private _historicalEmissionsLabel: string,
    private _cccModelUtility: CCCModelUtility,
    private _unitConversionService: UnitConversionService,
    private _measurementSystem: MeasurementSystem,
    private _targetLabel: string,
    private _projectedEmissionReductionLabel: string
  ) {
    this._boardYears = getBoardYearsFromTimeline(_timeline);
  }

  getData(node: ClientNode | ClientBoardContainer): YearByYearDataSeries {
    if (!this._calculatedSeries[node.id]) {
      if (!isClientBoardContainer(node) && isLeafNode(node)) {
        this._calculatedSeries[node.id] = this._getSeriesForLeafNode(node);
      } else {
        this._calculatedSeries[node.id] = this._getSeriesForNode(node);
      }
    }
    return this._calculatedSeries[node.id];
  }

  private _createSeries(
    node: ClientNode | ClientBoardContainer,
    bauData: Array<number | undefined>,
    abatementData: Array<number | undefined>
  ): YearByYearDataSeries {
    const returnValueIfApplicable = (year, value) => {
      if (year < this._timeline.scenarioStartYear) {
        return undefined;
      }
      return value;
    };
    const returnValueIfApplicableAsHistorical = (year, value) => {
      if (year > this._timeline.scenarioStartYear) {
        return undefined;
      }
      return value;
    };
    const emissionBau: Series = {
      data: bauData.map((Bau, index) => {
        const year = this._boardYears[index];
        return {
          value: returnValueIfApplicable(year, Bau ?? 0),
          label: `${year}`,
        };
      }),
      id: node.id,
      label: node.title,
    };
    const netCarbonAbatement: Series = {
      data: abatementData.map((abatement, index) => {
        const year = this._boardYears[index];
        return {
          value: returnValueIfApplicable(year, abatement ?? 0),
          label: `${year}`,
        };
      }),
      id: node.id,
      label: node.title,
    };
    //TODO: set title for remainingEmissions.
    const remainingEmissions: Series = {
      data: emissionBau.data.map((data, index) => {
        let value = undefined;
        if (data.value !== undefined) {
          //Rounding to whole grams to avoid a rendering problem that can occur of the grid has 0 as both max and min values and the data just consists of fractions of a gram.
          value = Math.round(data.value - netCarbonAbatement.data[index].value);
        }
        return {
          ...data,
          value,
        };
      }),
      id: node.id,
      label: '',
    };

    //TODO: set title for historicalEmissions, needed for accessibility later.
    let inheritedHistoricalEmissions = false;
    let historicalEmissionsValues = this._historicalEmissions[node.id];
    if (historicalEmissionsValues === undefined) {
      inheritedHistoricalEmissions = true;
      historicalEmissionsValues = this._getInheritedHistoricalEmissions(node.id) ?? [];
    }
    if (this._timeline.boardStartYear === this._timeline.scenarioStartYear) {
      historicalEmissionsValues = [];
    }
    const historicalEmissions: Series = {
      data: historicalEmissionsValues.map((v, i) => {
        const year = this._boardYears[i];
        return {
          value: returnValueIfApplicableAsHistorical(year, v),
          label: `${year}`,
        };
      }),
      id: node.id,
      label: this._historicalEmissionsLabel,
    };
    const abatementSeriesForRemainingEmissions = { ...netCarbonAbatement, id: ABATEMENT_SERIES_ID };
    const data: YearByYearDataSeries = {
      emissionBau,
      remainingEmissions,
      netCarbonAbatement,
      historicalEmissions,
      inheritedHistoricalEmissions,
      projectedEmissionReduction: { ...remainingEmissions, label: this._projectedEmissionReductionLabel },
      remainingEmissionsAndAbatement: [remainingEmissions, abatementSeriesForRemainingEmissions],
      remainingEmissionsAndChildAbatement: [remainingEmissions, abatementSeriesForRemainingEmissions],
      remainingEmissionsAndLeafNodeAbatement: [remainingEmissions, abatementSeriesForRemainingEmissions],
    };
    return data;
  }

  private _createSeriesForCustomModel(node: ClientNode): YearByYearDataSeries {
    const yearByYear = this._yearByYear[node.id];
    if (!holdsActiveCustomModel(node) || !yearByYear) {
      return this._getEmpty(node);
    }
    const customData = node.nodeProperties.customTargetModelData!;

    const dataSeries = this._createSeries(node, yearByYear.emissionBau, yearByYear.netCarbonAbatement);
    dataSeries.transitionData = this._createTransitionSeries(
      node.id,
      this._targetLabel,
      customData.transitionUnit,
      undefined,
      yearByYear.transition.target,
      yearByYear.transition.outcome
    );
    return dataSeries;
  }

  private _createSeriesForTransitionTarget(node: ClientNode): YearByYearDataSeries {
    const yearByYear = this._yearByYear[node.id];
    if (!yearByYear) {
      return this._getEmpty(node);
    }
    const te = node.nodeProperties.transitionElement!;
    let outcome = yearByYear.transition.outcome;
    let transition = yearByYear.transition.target;
    const ttProperties = node.nodeProperties.transitionTargetData!.transitionProperties;
    const dataSeries = this._createSeries(node, yearByYear.emissionBau, yearByYear.netCarbonAbatement);
    const transitionUnit = this._getTTUnit(te, ttProperties);
    const transitionKind = this._getTTKind(te, ttProperties);
    if (this._measurementSystem !== MeasurementSystem.Metric) {
      const metricUnit = this._cccModelUtility.getDefaultActivityUnit(te);
      transition = transition?.map((v) =>
        Math.round(
          this._unitConversionService.transformValue(MeasurementSystem.Metric, this._measurementSystem, metricUnit, v)
        )
      );
      outcome = outcome?.map((v) =>
        Math.round(
          this._unitConversionService.transformValue(MeasurementSystem.Metric, this._measurementSystem, metricUnit, v)
        )
      );
    }
    dataSeries.transitionData = this._createTransitionSeries(
      node.id,
      this._targetLabel,
      transitionUnit,
      transitionKind,
      transition,
      outcome
    );
    return dataSeries;
  }

  private _createSeriesFromLeafNodes(
    node: ClientNode | ClientBoardContainer,
    leafNodeSeries: YearByYearDataSeries[]
  ): YearByYearDataSeries {
    const toValues = (series: Series) => {
      return series.data.map((data) => data.value);
    };
    const joinValues = (values: number[][]) => {
      return values.reduce((joined: number[], rowValues: number[]) => {
        rowValues.forEach((value, index) => {
          const sum = joined[index] ?? 0;
          joined[index] = value !== undefined ? sum + value : undefined;
        });
        return joined;
      }, new Array());
    };
    const leafNodeBauValues = leafNodeSeries.map((data) => toValues(data.emissionBau));
    const leafNodeAbatementValues = leafNodeSeries.map((data) => toValues(data.netCarbonAbatement));
    return this._createSeries(node, joinValues(leafNodeBauValues), joinValues(leafNodeAbatementValues));
  }

  private _createTransitionSeries(
    nodeId: string,
    transitionLabel: string,
    transitionUnit: string,
    transitionKind?: string,
    targetValues?: (number | undefined)[],
    outcomeValues?: (number | undefined)[]
  ): DashboardTransitionData | undefined {
    const hasOutcomeValues = outcomeValues?.some((v) => v !== undefined) ?? false;
    const hasPlannedValues = targetValues?.some((v) => v !== undefined) ?? false;
    if (!hasOutcomeValues && !hasPlannedValues) {
      return undefined;
    }
    const outcome: Series | undefined = outcomeValues ? { data: [], id: nodeId, label: this._outcomeLabel } : undefined;
    const planned: Series = { data: [], id: nodeId, label: transitionLabel };
    this._boardYears.forEach((year, index) => {
      const label = `${year}`;
      planned.data.push({ label, value: targetValues[index] });
      if (outcome) {
        outcome.data.push({ label, value: outcomeValues[index] });
      }
    });

    const highlightedYearIndex = this._boardYears.indexOf(this._timeline.scenarioStartYear);
    let endYearValue = planned.data[planned.data.length - 1]?.value ?? undefined;
    let highlightedYearValue = planned.data[highlightedYearIndex]?.value;
    if (!highlightedYearValue || !endYearValue) {
      const filled = this._fillOutData(targetValues);
      if (!highlightedYearValue) {
        let hasFutureValue = false;
        for (let i = highlightedYearIndex; i < targetValues.length; i++) {
          if (targetValues[i] !== undefined) {
            hasFutureValue = true;
            break;
          }
        }
        if (hasFutureValue) {
          highlightedYearValue = filled[highlightedYearIndex];
        }
      }
      endYearValue = filled[filled.length - 1];
    }

    const currentYearOutcome = outcome?.data[highlightedYearIndex]?.value ?? undefined;
    return {
      planned: hasPlannedValues ? planned : undefined,
      outcome: hasOutcomeValues ? outcome : undefined,
      labelKind: transitionKind,
      labelUnit: transitionUnit,
      endYearTarget: endYearValue !== undefined ? Math.round(endYearValue) : undefined,
      highlightedYearAmbition: highlightedYearValue ? Math.round(highlightedYearValue) : undefined,
      highlightedYearOutcome: outcome?.data[highlightedYearIndex]?.value ?? undefined,
      differenceFromAmbition: this._getDiffOutcomePercentValue(currentYearOutcome, highlightedYearValue),
    };
  }
  //Leaving this for now, the fill is needed for info about leaf nodes and isn't directly it is duplicated for now, if needed in the future it will be replaced by a simpler function.
  //TODO: When not needed remove
  private _fillOutData(data: Array<number | undefined>): number[] {
    const firstValueIndex = data.findIndex((d) => d !== undefined);
    const firstValue = data[firstValueIndex] ?? 0;
    if (firstValueIndex === -1) {
      return data.map((d) => 0);
    }
    let lastValueIndex = firstValueIndex;
    let lastValue = firstValue;
    for (let i = data.length - 1; i > firstValueIndex; i--) {
      const val = data[i];
      if (val !== undefined) {
        lastValueIndex = i;
        lastValue = val;
        break;
      }
    }
    const getNextValueIndex = (index) => {
      for (let i = index + 1; i < data.length; i++) {
        if (data[i] !== undefined) return i;
      }
      return -1;
    };
    let prevValue = firstValue;
    let prevIndex = firstValueIndex;

    let nextIndex = getNextValueIndex(firstValueIndex);
    let nextValue = data[nextIndex];

    return data.map((value, index) => {
      if (index < firstValueIndex) {
        return firstValue;
      }
      if (index >= lastValueIndex) {
        return lastValue;
      }
      if (value !== undefined) {
        prevValue = value;
        prevIndex = index;
        nextIndex = getNextValueIndex(index);
        nextValue = data[nextIndex];
        return value;
      }
      const t = (index - prevIndex) / (nextIndex - prevIndex);
      return unclampedLerp(prevValue, nextValue, t);
    });
  }

  private _getDiffOutcomePercentValue(value: number | undefined, inRelationTo: number): number {
    const realValue = value ?? 0;
    const percentValue = 100 * ((realValue - inRelationTo) / inRelationTo);
    const roundedValue = Math.round(percentValue);
    return roundedValue;
  }

  /**
   * returns an empty series if the node doesn't have any data to calculate it.
   * Should only happen in preview when viewing an 'incomplete' board.
   * @param node
   * @returns
   */
  private _getEmpty(node: ClientNode | ClientBoardContainer): YearByYearDataSeries {
    const empty: Series = {
      data: this._boardYears.map((year, index) => {
        const value = year < this._timeline.scenarioStartYear ? undefined : 0;
        return {
          value,
          label: `${this._boardYears[index]}`,
        };
      }),
      id: node.id,
      label: '',
    };
    const data: YearByYearDataSeries = {
      emissionBau: empty,
      remainingEmissions: empty,
      projectedEmissionReduction: empty,
      historicalEmissions: empty,
      netCarbonAbatement: empty,
      inheritedHistoricalEmissions: false,
      remainingEmissionsAndAbatement: [empty, empty],
      remainingEmissionsAndChildAbatement: [empty, empty],
      remainingEmissionsAndLeafNodeAbatement: [empty, empty],
    };
    return data;
  }

  private _getInheritedHistoricalEmissions(nodeId: string): number[] | undefined {
    const getInheritedEmissions = (nodeId: string) => {
      nodeId = this._parentIds[nodeId];
      if (nodeId) {
        const historicalEmissions = this._historicalEmissions[nodeId];
        if (historicalEmissions) {
          return historicalEmissions;
        }
        return getInheritedEmissions(nodeId);
      }
      return undefined;
    };
    return getInheritedEmissions(nodeId);
  }
  private _getSeriesForLeafNode(node: ClientNode): YearByYearDataSeries {
    if (holdsInitialisedUtilisationTE(node)) {
      return this._createSeriesForTransitionTarget(node);
    } else if (holdsCustomModel(node)) {
      return this._createSeriesForCustomModel(node);
    }
    return this._getEmpty(node);
  }

  private _getSeriesForNode(node: ClientNode | ClientBoardContainer): YearByYearDataSeries {
    const children = getChildren(this._board, node);
    const leafNodes = getLeafNodes(this._board, isClientNode(node) ? node : undefined);
    // reversing because series are stacked from bottom up in diagrams and we want it to match the top down stacking in the navigation.
    children.reverse();
    leafNodes.reverse();

    const leafNodeSeries = leafNodes.map((l) => this.getData(l));
    const dataSeries = this._createSeriesFromLeafNodes(node, leafNodeSeries);

    const leafNodeNetCarbonAbatement = leafNodeSeries.map((data) => data.netCarbonAbatement);
    const leafNodeRemainingEmissions = leafNodeSeries.map((data) => data.remainingEmissions);
    const childData = children.map((c) => this.getData(c));
    const childNetCarbonAbatement = childData.length ? childData.map((data) => data.netCarbonAbatement) : undefined;
    const childRemainingEmissions = childData.length ? childData.map((data) => data.remainingEmissions) : undefined;
    let childHistoricalEmissions;
    if (childData.every((c) => !c.inheritedHistoricalEmissions)) {
      childHistoricalEmissions = childData.map((c) => c.historicalEmissions);
    }
    return {
      ...dataSeries,
      historicalEmissions: dataSeries.historicalEmissions,
      childHistoricalEmissions,
      childNetCarbonAbatement,
      childRemainingEmissions,
      childRemainingEmissionsAndAbatement: [
        ...childRemainingEmissions,
        { ...dataSeries.netCarbonAbatement, id: ABATEMENT_SERIES_ID },
      ],
      leafNodeRemainingEmissions,
      remainingEmissionsAndChildAbatement: [dataSeries.remainingEmissions, ...childNetCarbonAbatement],
      remainingEmissionsAndLeafNodeAbatement: [dataSeries.remainingEmissions, ...leafNodeNetCarbonAbatement],
    };
  }
}
